import { useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import { useFeatureIsOn } from "@growthbook/growthbook-react";

import { GetContentDocument } from "../graphql/getContent.generated";
import { GetMyContentAccessDocument } from "../graphql/getMyContentAccess.generated";
import { featuredPlanIn } from "@/features/products/components/organism/PurchaseCard/pricing";
import { useUserCurrencyResolution } from "@/features/products/utils/userCurrency";
import { checkoutV2YunoFeatureFlag } from "@/features/products/constants/featureFlags";
import { useHomepageSsr } from "@/providers/HomepageSsrProvider";
import { useUser } from "@/providers/useUser";

type DisplayPrice = { value: number; currency: string };

export const useContentPaywall = (contentId: string) => {
  const flagOn = useFeatureIsOn(checkoutV2YunoFeatureFlag);
  const { user } = useUser();
  const { contentLanding, isSsrContentLanding } = useHomepageSsr();
  const userId = user?.id ?? null;
  const { currency: userCurrency, isResolved: isCurrencyResolved } =
    useUserCurrencyResolution();
  const hasServerContent = Boolean(
    contentId &&
      isSsrContentLanding &&
      contentLanding?.contentResolved &&
      contentLanding.requestedId === contentId
  );
  const { data, loading: contentLoading } = useQuery(GetContentDocument, {
    variables: { id: contentId },
    skip: hasServerContent,
  });
  const {
    data: accessData,
    loading: accessLoading,
    error: accessError,
    refetch: refetchAccess,
  } = useQuery(GetMyContentAccessDocument, {
    variables: { contentId },
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    skip: !flagOn || !userId,
  });
  const previousUserIdRef = useRef(userId);
  const accessIdentityChanged = previousUserIdRef.current !== userId;

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    if (!flagOn || !userId || !previousUserId || previousUserId === userId) {
      return;
    }
    void refetchAccess({ contentId }).catch(() => undefined);
  }, [contentId, flagOn, refetchAccess, userId]);

  const content =
    data?.content ??
    (hasServerContent ? contentLanding?.content ?? null : null);
  const prices = content?.prices ?? [];
  const subscriptionPlans = content?.subscriptionPlans ?? [];
  const skuId = content?.skuId ?? null;
  const shouldCheckAccess = flagOn && Boolean(userId);
  const accessKnown =
    !shouldCheckAccess ||
    (!accessIdentityChanged &&
      !accessLoading &&
      !accessError &&
      accessData?.myContentAccess != null);
  const hasAccess = shouldCheckAccess
    ? accessData?.myContentAccess.hasAccess ?? false
    : false;

  // Cheapest first from the resolver, so the landing advertises the entry price
  // the same way the checkout's picker does.
  const subscriptionPlan = featuredPlanIn(subscriptionPlans, userCurrency);

  const displayPrice: DisplayPrice | null = subscriptionPlan
    ? { value: subscriptionPlan.price, currency: subscriptionPlan.currency }
    : prices.find((p) => (p.currency as string) === userCurrency) ??
      prices.find((p) => (p.currency as string) === "USD") ??
      prices[0] ??
      null;

  // Being sold by subscription is itself the paywall. Its `sku_price` rows are
  // deactivated at materialization, so a subscription product reaches this hook
  // with no price at all and used to read as free.
  const isPaid =
    subscriptionPlan != null ||
    (displayPrice != null && displayPrice.value > 0);
  const loading =
    (contentLoading && !hasServerContent) ||
    (shouldCheckAccess && !accessKnown) ||
    !isCurrencyResolved;
  const isPaywalled =
    !loading && flagOn && isPaid && !hasAccess && Boolean(skuId);

  return {
    loading,
    isPaywalled,
    skuId,
    displayPrice,
    subscriptionPlan,
  };
};
